import { Head, Link, router } from "@inertiajs/react"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import { newRecipeImportPath, newRecipePath, recipesPath } from "@/routes"
import type { BreadcrumbItem, RecipeImport } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Recipes", href: recipesPath() },
  { title: "Importing...", href: "#" },
]

const COOKING_MESSAGES = [
  "Preheating the oven...",
  "Chopping the onions...",
  "Measuring the flour...",
  "Cracking the eggs...",
  "Simmering the sauce...",
  "Tasting for seasoning...",
  "Folding in the cheese...",
  "Whisking vigorously...",
  "Letting it rest...",
  "Deglazing the pan...",
  "Zesting the lemon...",
  "Kneading the dough...",
  "Caramelizing the sugar...",
  "Toasting the spices...",
  "Reducing the broth...",
  "Blanching the vegetables...",
  "Infusing the herbs...",
  "Basting with butter...",
]

function useRotatingMessage(active: boolean) {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * COOKING_MESSAGES.length))

  useEffect(() => {
    if (!active) return

    const timer = setInterval(() => {
      setIndex((prev) => {
        let next: number
        do {
          next = Math.floor(Math.random() * COOKING_MESSAGES.length)
        } while (next === prev && COOKING_MESSAGES.length > 1)
        return next
      })
    }, 2500)
    return () => clearInterval(timer)
  }, [active])

  return COOKING_MESSAGES[index]
}

interface Props {
  recipe_import: RecipeImport
}

export default function Show({ recipe_import }: Props) {
  const isLoading =
    recipe_import.status === "pending" || recipe_import.status === "processing"
  const message = useRotatingMessage(isLoading)

  useEffect(() => {
    if (recipe_import.status === "completed") {
      router.visit(newRecipePath() + `?import_id=${recipe_import.id}`)
      return
    }

    if (isLoading) {
      const timer = setInterval(() => {
        router.reload({ only: ["recipe_import"] })
      }, 2000)
      return () => clearInterval(timer)
    }
  }, [recipe_import.status, recipe_import.id, isLoading])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Importing Recipe" />

      <div className="flex flex-col items-center justify-center p-12">
        {isLoading && (
          <>
            <div className="mb-6 text-6xl animate-bounce">
              🍳
            </div>
            <h2 className="mb-3 text-xl font-semibold">Importing recipe...</h2>
            <p
              key={message}
              className="text-muted-foreground animate-fade-in text-center italic"
            >
              {message}
            </p>
          </>
        )}

        {recipe_import.status === "failed" && (
          <>
            <h2 className="mb-2 text-xl font-semibold text-red-600">Import failed</h2>
            <p className="text-muted-foreground mb-4">
              {recipe_import.error_message ?? "An unknown error occurred"}
            </p>
            <Button asChild>
              <Link href={newRecipeImportPath()}>Try Again</Link>
            </Button>
          </>
        )}
      </div>
    </AppLayout>
  )
}
